import React from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getAnalytics, isSupported } from "firebase/analytics";

// Constants
const ALLOWED_EMAIL_DOMAIN =
  process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || "@neutrinodesign.com";
const IS_DEV = process.env.NODE_ENV === "development";

// Initialize Firebase
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID, // Optional but recommended for Analytics
};

// Conditional logging helper
const log = {
  info: (...args) => IS_DEV && console.log("[FirebaseAuth]", ...args),
  warn: (...args) => console.warn("[FirebaseAuth]", ...args),
  error: (...args) => console.error("[FirebaseAuth]", ...args),
};

// Log config (without exposing sensitive data)
log.info("Config check:", {
  hasApiKey: !!firebaseConfig.apiKey,
  authDomain: firebaseConfig.authDomain || "MISSING",
  projectId: firebaseConfig.projectId || "MISSING",
  hasAppId: !!firebaseConfig.appId,
  currentHost: typeof window !== "undefined" ? window.location.hostname : "N/A",
});

let app = null;
let auth = null;
let analytics = null;

if (
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId &&
  firebaseConfig.appId
) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    log.info("Firebase initialized successfully");
    // Explicitly set persistence to localStorage (this is the default, but being explicit)
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      log.error("Failed to set persistence:", error);
    });

    // Initialize Analytics (only in browser environment, if API key is valid, and measurementId is provided)
    // Analytics is optional - if it fails, the app will continue to work without it
    if (typeof window !== "undefined" && firebaseConfig.apiKey && firebaseConfig.measurementId) {
      // Delay Analytics initialization slightly to avoid blocking app startup
      // This also helps catch any async errors more gracefully
      setTimeout(() => {
        isSupported()
          .then((supported) => {
            if (supported) {
              try {
                analytics = getAnalytics(app);
                log.info("Firebase Analytics initialized successfully");
              } catch (error) {
                // Analytics is optional, so we don't throw - just log and continue
                log.warn("Firebase Analytics initialization failed (non-critical):", error.message);
                analytics = null;
              }
            } else {
              log.warn("Firebase Analytics is not supported in this environment");
            }
          })
          .catch((error) => {
            // Analytics is optional, so we don't throw - just log and continue
            // This catches errors from isSupported() or getAnalytics()
            log.warn("Firebase Analytics initialization failed (non-critical):", error.message);
            analytics = null;
          });
      }, 100);
    } else if (typeof window !== "undefined" && !firebaseConfig.measurementId) {
      log.info("Firebase Analytics skipped: measurementId not provided (optional)");
    } else if (typeof window !== "undefined" && !firebaseConfig.apiKey) {
      log.info("Firebase Analytics skipped: API key not provided");
    }
  } catch (error) {
    log.error("Error initializing Firebase:", error);
  }
} else {
  log.warn("Firebase config env vars not set. App will run without auth.");
  log.warn("Missing vars:", {
    apiKey: !firebaseConfig.apiKey,
    authDomain: !firebaseConfig.authDomain,
    projectId: !firebaseConfig.projectId,
    appId: !firebaseConfig.appId,
  });
}

// Email validation helper
function isValidEmail(email) {
  if (!email || typeof email !== "string") {
    log.warn("Invalid email provided to isValidEmail:", email);
    return false;
  }
  const isValid = email
    .toLowerCase()
    .endsWith(ALLOWED_EMAIL_DOMAIN.toLowerCase());
  if (!isValid) {
    log.info(
      "Email validation failed:",
      email,
      "does not end with",
      ALLOWED_EMAIL_DOMAIN
    );
  }
  return isValid;
}

// Check if email is allowed (either @neutrinodesign.com or invited)
async function isEmailAllowed(email) {
  if (!email || typeof email !== "string") {
    return false;
  }

  // Always allow @neutrinodesign.com emails
  if (isValidEmail(email)) {
    return true;
  }

  // Check if user was invited through team page
  try {
    const base = process.env.REACT_APP_API_BASE || "";
    const response = await fetch(
      `${base}/api/teams?email=${encodeURIComponent(
        email.toLowerCase().trim()
      )}`
    );
    if (response.ok) {
      const data = await response.json();
      if (
        data.invited &&
        (data.status === "pending" || data.status === "active")
      ) {
        log.info("Email is allowed via team invitation:", email);
        return true;
      }
    }
  } catch (err) {
    log.error("Error checking invitation status:", err);
    // If we can't check, deny access for safety
    return false;
  }

  return false;
}

// Helper to sign out invalid users
async function signOutInvalidUser(reason) {
  if (!auth) return;
  log.warn("Signing out invalid user:", reason);
  try {
    await signOut(auth);
  } catch (err) {
    log.error("Error signing out invalid user:", err);
  }
}

// Export analytics instance for use elsewhere
export { analytics };

const AuthContext = React.createContext({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
  getIdToken: async () => null,
});

export function useFirebaseAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useFirebaseAuth must be used within FirebaseAuthProvider");
  }
  return context;
}

export function FirebaseAuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    let unsubscribe = null;

    // Helper function to handle auth state changes with email validation
    function handleAuthStateChange(firebaseUser) {
      if (!isMounted) return;

      // If no user, set state immediately
      if (!firebaseUser) {
        log.info("Auth state changed: not authenticated");
        setUser(null);
        setLoading(false);
        return;
      }

      // Validate email domain or invitation if user exists
      if (!firebaseUser.email) {
        log.warn("User object missing email property:", firebaseUser);
        setUser(null);
        setLoading(false);
        return;
      }

      // Check if email is allowed (async)
      isEmailAllowed(firebaseUser.email)
        .then((allowed) => {
          if (!isMounted) return;

          if (!allowed) {
            signOutInvalidUser(
              `Email not allowed: ${firebaseUser.email}. You must either use a ${ALLOWED_EMAIL_DOMAIN} email address or be invited through the team page.`
            );
            setUser(null);
            setLoading(false);
            return;
          }

          log.info(
            "Auth state changed:",
            `authenticated as ${firebaseUser.email}`
          );
          setUser(firebaseUser);
          setLoading(false);
        })
        .catch((err) => {
          if (!isMounted) return;
          log.error("Error checking email permission:", err);
          signOutInvalidUser(`Error validating email: ${firebaseUser.email}`);
          setUser(null);
          setLoading(false);
        });
    }

    // Set up auth state listener - this will fire immediately with current user (or null)
    unsubscribe = onAuthStateChanged(auth, handleAuthStateChange, (error) => {
      if (!isMounted) return;
      log.error("Auth state change error:", error.code, error.message);

      if (error.code === "auth/unauthorized-domain") {
        log.error(
          "Domain not authorized. Add your domain to Authentication > Settings > Authorized domains"
        );
      }

      setLoading(false);
    });

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const signIn = React.useCallback(async () => {
    if (!auth) {
      log.warn("Firebase auth not initialized");
      const missingVars = [];
      if (!firebaseConfig.apiKey) missingVars.push('REACT_APP_FIREBASE_API_KEY');
      if (!firebaseConfig.authDomain) missingVars.push('REACT_APP_FIREBASE_AUTH_DOMAIN');
      if (!firebaseConfig.projectId) missingVars.push('REACT_APP_FIREBASE_PROJECT_ID');
      if (!firebaseConfig.appId) missingVars.push('REACT_APP_FIREBASE_APP_ID');
      
      throw new Error(
        `Firebase authentication is not configured. Missing environment variables: ${missingVars.join(', ')}. Please create a .env.local file in the project root with these variables. See README.md for details.`
      );
    }
    
    // Check if API key is valid before attempting sign-in
    if (!firebaseConfig.apiKey || firebaseConfig.apiKey === 'undefined' || firebaseConfig.apiKey.trim() === '') {
      throw new Error(
        'Firebase API key is missing or invalid. Please check your .env.local file and ensure REACT_APP_FIREBASE_API_KEY is set correctly. You can find your API key in Firebase Console > Project Settings > General > Your apps > Web app.'
      );
    }
    try {
      log.info("Starting sign-in with popup...");
      const provider = new GoogleAuthProvider();
      // Add custom parameters to ensure popup works correctly
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      log.info("Popup sign-in completed, validating email...");

      // Validate email domain or invitation after popup sign-in
      const allowed = await isEmailAllowed(result.user.email);
      if (!allowed) {
        await signOutInvalidUser(`Email not allowed: ${result.user.email}`);
        throw new Error(
          `Access restricted. You must either use a ${ALLOWED_EMAIL_DOMAIN} email address or be invited through the team page.`
        );
      }

      log.info("Sign-in successful:", result.user.email);
    } catch (error) {
      log.error("Sign in error:", error.code, error.message);
      // Provide more helpful error messages
      if (error.code === 'auth/api-key-not-valid') {
        throw new Error('Firebase API key is not valid. Please check your .env.local file and ensure REACT_APP_FIREBASE_API_KEY is set correctly. See README.md for setup instructions.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw error; // Re-throw to be handled by caller (they may want to ignore this)
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error(`This domain (${window.location.hostname}) is not authorized in Firebase. Please add it to Firebase Console > Authentication > Settings > Authorized domains.`);
      }
      throw error;
    }
  }, []);

  const handleSignOut = React.useCallback(async () => {
    if (!auth) {
      log.warn("Firebase auth not initialized");
      return;
    }
    try {
      await signOut(auth);
      log.info("Sign-out successful");
    } catch (error) {
      log.error("Sign out error:", error);
      throw error;
    }
  }, []);

  const getIdToken = React.useCallback(
    async (forceRefresh = false) => {
      if (!auth || !user) {
        return null;
      }
      try {
        return await user.getIdToken(forceRefresh);
      } catch (error) {
        log.error("Error getting ID token:", error);
        return null;
      }
    },
    [user]
  );

  const value = React.useMemo(
    () => ({
      user,
      loading,
      signIn,
      signOut: handleSignOut,
      getIdToken,
    }),
    [user, loading, signIn, handleSignOut, getIdToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }) {
  const { user, loading } = useFirebaseAuth();
  const [checkingInvitation, setCheckingInvitation] = React.useState(false);
  const [emailAllowed, setEmailAllowed] = React.useState(null);

  // Check if email is allowed (async check for invitations)
  React.useEffect(() => {
    if (loading) {
      // Still loading auth state, don't check email yet
      setEmailAllowed(null);
      return;
    }

    if (!user || !user.email) {
      // No user, mark as not allowed (will show login page)
      setEmailAllowed(false);
      return;
    }

    // If it's a @neutrinodesign.com email, allow immediately
    if (isValidEmail(user.email)) {
      setEmailAllowed(true);
      return;
    }

    // Otherwise check invitation status
    setCheckingInvitation(true);
    isEmailAllowed(user.email)
      .then((allowed) => {
        setEmailAllowed(allowed);
        if (!allowed && user) {
          log.warn("User email not allowed:", user.email);
        }
      })
      .catch((err) => {
        log.error("Error checking email permission:", err);
        setEmailAllowed(false);
      })
      .finally(() => {
        setCheckingInvitation(false);
      });
  }, [user, loading]);

  // Show loading only when we're actively checking auth state or invitation
  if (loading || checkingInvitation) {
    return (
      <div className="center" style={{ minHeight: "100vh" }}>
        <div>Loading…</div>
      </div>
    );
  }

  // If emailAllowed is still null after loading is done, something went wrong
  // But if there's no user, we know we should show login
  if (emailAllowed === null && !user) {
    return <LoginPage />;
  }

  // Check if user exists and has valid email or invitation
  if (!user || !user.email || !emailAllowed) {
    if (user && (!user.email || !emailAllowed)) {
      log.warn("User missing email or not allowed:", user.email);
    }
    return <LoginPage />;
  }

  return <>{children}</>;
}

function LoginPage() {
  const { signIn } = useFirebaseAuth();
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSignIn = async () => {
    setIsSigningIn(true);
    setError("");

    try {
      await signIn();
      // Success - user is now authenticated
      // The onAuthStateChanged listener will update the state
    } catch (err) {
      // Don't show error for popup closed by user
      if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || "Failed to sign in. Please try again.");
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  // Check if Firebase is configured
  const isFirebaseConfigured = !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  );

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">Welcome to Neutrino Uptime</h1>
        <p className="login-subtitle">
          Sign in with your Google account to continue
        </p>

        {!isFirebaseConfigured && (
          <div className="login-error" style={{ marginBottom: "16px" }}>
            <strong>Firebase authentication is not configured.</strong>
            <br />
            <br />
            For local development, create a <code>.env.local</code> file in the
            project root with the following variables:
            <br />
            <br />
            <code>
              REACT_APP_FIREBASE_API_KEY=your-api-key
              <br />
              REACT_APP_FIREBASE_AUTH_DOMAIN=your-project.firebaseapp.com
              <br />
              REACT_APP_FIREBASE_PROJECT_ID=your-project-id
              <br />
              REACT_APP_FIREBASE_APP_ID=your-app-id
            </code>
            <br />
            <br />
            You can find these values in Firebase Console → Project Settings →
            General → Your apps. See README.md for more details.
          </div>
        )}

        {error && <div className="login-error">{error}</div>}

        <button
          className="btn btn-primary login-button"
          onClick={handleSignIn}
          disabled={isSigningIn || !isFirebaseConfigured}
        >
          {isSigningIn ? (
            <>
              <span
                className="checking-spinner"
                style={{ width: "20px", height: "20px" }}
              ></span>
              Signing in...
            </>
          ) : (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="login-button-icon"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign in with Google
            </>
          )}
        </button>

        <p className="login-footer">
          By signing in, you agree to use this service responsibly.
        </p>
      </div>
    </div>
  );
}

export function LoginLogout() {
  const { user, signIn, signOut: handleSignOut } = useFirebaseAuth();

  if (!auth) return null;

  if (!user) {
    return (
      <button className="btn" onClick={signIn}>
        Log in
      </button>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <span>{user.displayName || user.email || "User"}</span>
      <button className="btn" onClick={handleSignOut}>
        Log out
      </button>
    </div>
  );
}
